import getConfig from 'next/config'

import type { Context as CartContext } from '@grandvisionhq/carts-v2'
import { extractSkuQuantityPairsFromLineItems } from '@grandvisionhq/carts-v2'
import type {
  CheckoutSettings,
  Consent,
  Routes,
  ShippingMethodsLogos,
} from '@grandvisionhq/checkout-v2'
import { AddressLineFormat } from '@grandvisionhq/checkout-v2'
import { IntlContextModel } from '@grandvisionhq/state'
import { Util as StoreUtils } from '@grandvisionhq/stores-v2'
interface SettingsInput {
  cart: CartContext.CartContextModel['cart']
  intl: IntlContextModel
  shippingMethodsLogos?: ShippingMethodsLogos
}

const { publicRuntimeConfig } = getConfig()
const { orderTimeOut } = publicRuntimeConfig.checkout

export const basePath = '/checkout'
export const routes: Routes = {
  login: {
    label: 'Informations',
    path: `${basePath}/login`,
  },
  shipping: {
    label: 'Livraison',
    path: `${basePath}/livraison`,
  },
  overview: {
    label: 'Récapitulatif',
    path: `${basePath}/recapitulatif`,
  },
  payment: {
    label: 'Paiement',
    path: `${basePath}/paiement`,
  },
}

const getCheckoutConsents = ({
  cart,
  intl,
}: {
  cart: CartContext.CartContextModel['cart']
  intl: IntlContextModel
}): Consent[] => {
  const { getLabel, getFormattedLabel } = intl
  const hasContactLenses = cart?.contains.contactLenses
  const hasContactLensesSubscription = cart?.contains.contactLensesSubscription

  const allConsents: Consent[] = [
    {
      title: getFormattedLabel('checkout.consent.conditions', {
        parseLink: true,
        openLinksInNewTab: true,
        parseHTML: false,
      }),
      value: 'terms-conditions',
      required: true,
      errorMessage: getLabel('checkout.consent.conditions.error'),
    },
  ]

  if (hasContactLenses || hasContactLensesSubscription) {
    allConsents.push({
      title: getLabel('checkout.consent.prescription'),
      value: 'cl-prescription-consent',
      required: true,
      errorMessage: getLabel('checkout.consent.prescription.error'),
    })
  }

  return allConsents
}

export const getCheckoutConfig = ({
  cart,
  intl,
  shippingMethodsLogos,
}: SettingsInput): CheckoutSettings => {
  let shippingDetails: CheckoutSettings['shippingDetails'] | undefined

  if (cart && (cart.billingAddress || cart.shippingAddress || cart.customerDetails)) {
    shippingDetails = {
      ...(cart.billingAddress ? { billingAddress: cart.billingAddress } : {}),
      ...(cart.shippingAddress ? { deliveryAddress: cart.shippingAddress } : {}),
      ...(cart.customerDetails ? { customer: cart.customerDetails } : {}),
    }
  }

  const itemsToBeShipped = cart?.lineItems
    ? extractSkuQuantityPairsFromLineItems(cart.lineItems)
    : []

  return {
    account: {
      implicitAccountCreation: true,
      createAccountRequired: true,
    },
    consent: {
      items: getCheckoutConsents({ intl, cart }),
      implicitTermsAndConditions: false,
    },
    pickupPoints: {
      showCityField: true,
    },
    shipping: {
      explicitShippingMethod: true,
    },
    shippingDetails: shippingDetails ? shippingDetails : {},
    shippingMethodsLogos: shippingMethodsLogos ? shippingMethodsLogos : {},
    storeFinder: {
      showUseMyLocation: true,
      searchWhitelist: ['storeName', 'postalCode', 'streetName', 'town'] as StoreUtils.SearchKey[],
    },
    ...(orderTimeOut ? { orderCreationTimeOutLimit: orderTimeOut } : {}),
    features: {
      isCompanyEnabled: true,
      isServiceStoreEnabled: false,
    },
    itemsToBeShipped,
    addressLookup: {
      addressLineFormat: AddressLineFormat.streetNumber_streetName,
      shouldCombineAddress: true,
    },
    login: {
      disableSignup: true,
      returnPath: cart ? `${routes.shipping?.path}?cartId=${cart.id}` : routes.shipping?.path,
    },
    routes,
    logoVariant: 'alternative',
  }
}
